import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { useEscapeKey, useLockBodyScroll } from '@/hooks'
import { IconButton } from './IconButton'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Off for a destructive confirm, so a stray click cannot dismiss it. */
  closeOnOverlayClick?: boolean
}

const SIZES = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' } as const

/**
 * A blocking decision or a short form.
 *
 * On phones it docks to the bottom and takes the full width — a centred dialog
 * on a 375px screen leaves no room for its own content. Anything longer than
 * about two fields belongs in a Drawer or its own route.
 *
 * Portalled to `document.body` for the same reason as Drawer: `fixed inset-0`
 * means the viewport only while no ancestor carries a transform, filter or
 * backdrop-filter. Today's two callers sit in the page body and would render
 * correctly either way; the next one opened from the blurred top bar would not.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
}: ModalProps) {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)
  useEscapeKey(onClose, isOpen)
  useLockBodyScroll(isOpen)

  // Move focus into the dialog on open so the keyboard is not left on the page
  // behind the overlay.
  useEffect(() => {
    if (!isOpen) return
    const previous = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    return () => previous?.focus?.()
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 animate-fade-in bg-black/45"
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative w-full animate-slide-in-bottom rounded-t-xl border border-border bg-surface-raised shadow-lg',
          'max-h-[92dvh] overflow-y-auto outline-none',
          'sm:animate-scale-in sm:rounded-xl',
          SIZES[size],
        )}
      >
        <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-fg">{title}</h2>
            {description && <p className="mt-1 text-base text-fg-muted">{description}</p>}
          </div>
          <IconButton label={t('common.close')} icon={<X />} size="sm" onClick={onClose} />
        </div>
        <div className="px-5 pb-5">{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
